import type { NextFunction, Request, Response } from 'express';
import { ZodError } from 'zod';
import type { Logger } from './logger.js';

export class HttpError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

export const notFound = (message = 'Not found') => new HttpError(404, 'NOT_FOUND', message);
export const forbidden = (message = 'Forbidden') => new HttpError(403, 'FORBIDDEN', message);

export function notFoundHandler(_req: Request, _res: Response, next: NextFunction) {
  next(notFound('Route not found'));
}

export function errorHandler(logger: Logger) {
  // Express identifies error middleware by its 4-argument signature.
  return (err: unknown, req: Request, res: Response, _next: NextFunction) => {
    if (err instanceof ZodError) {
      return res.status(400).json({
        error: {
          code: 'VALIDATION_ERROR',
          message: 'Invalid request',
          details: err.issues.map((i) => ({ path: i.path.join('.'), message: i.message })),
        },
      });
    }
    if (err instanceof HttpError) {
      return res
        .status(err.status)
        .json({ error: { code: err.code, message: err.message, details: err.details } });
    }
    // Errors raised by Express middleware (payload too large, malformed JSON) already carry a 4xx status.
    const status =
      (err as { status?: number; statusCode?: number })?.status ??
      (err as { statusCode?: number })?.statusCode;
    if (typeof status === 'number' && status >= 400 && status < 500) {
      const type = (err as { type?: string }).type;
      const code =
        type === 'entity.too.large'
          ? 'PAYLOAD_TOO_LARGE'
          : type === 'entity.parse.failed'
            ? 'INVALID_JSON'
            : 'BAD_REQUEST';
      const message =
        type === 'entity.too.large'
          ? 'The request is too large'
          : type === 'entity.parse.failed'
            ? 'The request body is not valid JSON'
            : 'Bad request';
      return res.status(status).json({ error: { code, message } });
    }
    if (process.env.DEBUG_TEST_ERRORS) console.error('UNHANDLED', err);
    logger.error({ err, url: req.originalUrl }, 'Unhandled error');
    return res
      .status(500)
      .json({ error: { code: 'INTERNAL_ERROR', message: 'Something went wrong' } });
  };
}
